import { useState } from 'react';
import { useTheme } from './ThemeProvider';
import { Sun, Moon, BarChart3, TrendingUp, Wallet, RefreshCw, ListPlus, LineChart } from 'lucide-react';
import { usePriceRefresh } from '../hooks/usePriceRefresh';
import { TSLActivityButton } from './TSLActivityButton';
import { ExportTradingViewDialog } from './ExportTradingViewDialog';
import { WebSocketHealthWidget } from './WebSocketHealthWidget';

interface HeaderProps {
  selectedView: 'overview' | 'trading' | 'positions' | 'charts';
  onViewChange: (view: 'overview' | 'trading' | 'positions' | 'charts') => void;
}

export function Header({ selectedView, onViewChange }: HeaderProps) {
  const { isDarkMode, toggleTheme } = useTheme();
  const { state: priceRefreshState, controls: priceRefreshControls } = usePriceRefresh();
  const [isExportOpen, setIsExportOpen] = useState(false);

  const navItems = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'trading', label: 'Trading', icon: TrendingUp },
    { id: 'positions', label: 'Positions', icon: Wallet },
    { id: 'charts', label: 'Charts', icon: LineChart },
  ] as const;

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Title */}
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                MEXC Trading Dashboard
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Futures Trading Management
              </p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = selectedView === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => onViewChange(item.id)}
                  className={`
                    flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors
                    ${isActive
                      ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Controls */}
          <div className="flex items-center space-x-3">
            {/* WebSocket Health Widget */}
            <div className="hidden lg:block">
              <WebSocketHealthWidget compact className="mr-2" />
            </div>

            {/* TSL Activity Log */}
            <TSLActivityButton />

            {/* Export for TradingView */}
            <button
              onClick={() => setIsExportOpen(true)}
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
              aria-label="Export for TradingView"
              title="Export all active USDT Perp pairs for TradingView watchlist import"
            >
              <ListPlus className="w-4 h-4" />
              <span className="text-sm font-medium">Export for TradingView</span>
            </button>

            {/* Manual Price Refresh */}
            <button
              onClick={priceRefreshControls.manualRefresh}
              disabled={priceRefreshState.isRefreshing}
              className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Refresh prices now"
              title="Refresh all prices immediately"
            >
              <RefreshCw className={`w-5 h-5 ${priceRefreshState.isRefreshing ? 'animate-spin' : ''}`} />
            </button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              aria-label="Toggle theme"
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
        {/* Export Dialog */}
        {isExportOpen && (
          <ExportTradingViewDialog isOpen={isExportOpen} onClose={() => setIsExportOpen(false)} />
        )}

        {/* Mobile Navigation */}
        <div className="md:hidden border-t border-gray-200 dark:border-gray-700">
          <nav className="flex items-center justify-around py-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = selectedView === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => onViewChange(item.id)}
                  className={`
                    flex flex-col items-center space-y-1 px-3 py-2 rounded-lg transition-colors
                    ${isActive
                      ? 'text-primary-700 dark:text-primary-300'
                      : 'text-gray-600 dark:text-gray-400'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-xs font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    </header>
  );
}
